/**
 * Pure logic for Convert Image, shared by the website tool and the API (utility-api/core/synced/convertCore.ts is a
 * generated copy: edit THIS file, then run `node scripts/sync-core.mjs` inside utility-api).
 * No DOM and no Node-only calls: only Uint8Array / DataView, so it runs in a browser tab, in Node and in tests.
 *
 * Contents: format table, colour parsing, option validation, resize geometry, and byte-level writers/readers
 * for BMP, ICO, TIFF, GIF (single frame) and PNG/JPEG metadata helpers.
 */

// ---------------------------------------------------------------- formats

export type OutFormat = "png" | "jpeg" | "webp" | "avif" | "gif" | "tiff" | "bmp" | "ico";

export interface FormatInfo {
  id: OutFormat;
  mime: string;
  ext: string;
  label: string;
  /** Can the file keep see-through pixels? */
  alpha: boolean;
  /** Does the quality setting apply? */
  lossy: boolean;
  /** One plain-language line for the format picker. */
  hint: string;
}

export const FORMATS: readonly FormatInfo[] = [
  { id: "png", mime: "image/png", ext: "png", label: "PNG", alpha: true, lossy: false, hint: "Sharp edges and see-through backgrounds. Best for logos, icons and screenshots." },
  { id: "jpeg", mime: "image/jpeg", ext: "jpg", label: "JPEG", alpha: false, lossy: true, hint: "Small files for photos. No see-through background, so a fill colour is used." },
  { id: "webp", mime: "image/webp", ext: "webp", label: "WebP", alpha: true, lossy: true, hint: "Smaller than JPEG or PNG at similar looks. Accepted by most websites." },
  { id: "avif", mime: "image/avif", ext: "avif", label: "AVIF", alpha: true, lossy: true, hint: "Smallest files of all. Newer, so some older programs cannot open it." },
  { id: "gif", mime: "image/gif", ext: "gif", label: "GIF", alpha: true, lossy: false, hint: "Limited to 256 colours. Fine for simple graphics." },
  { id: "bmp", mime: "image/bmp", ext: "bmp", label: "BMP", alpha: false, lossy: false, hint: "Plain uncompressed picture for old programs. Files are large." },
  { id: "ico", mime: "image/x-icon", ext: "ico", label: "ICO", alpha: true, lossy: false, hint: "Website and Windows icon with several sizes packed into one file." },
  { id: "tiff", mime: "image/tiff", ext: "tiff", label: "TIFF", alpha: true, lossy: false, hint: "Uncompressed print and archive format. Files are large." },
];

export const FORMAT_IDS: readonly OutFormat[] = FORMATS.map((f) => f.id);

export function formatInfo(id: OutFormat): FormatInfo {
  return FORMATS.find((f) => f.id === id)!;
}

const FORMAT_ALIASES: Record<string, OutFormat> = {
  png: "png", jpeg: "jpeg", jpg: "jpeg", jpe: "jpeg", jfif: "jpeg", webp: "webp", avif: "avif", gif: "gif",
  tiff: "tiff", tif: "tiff", bmp: "bmp", ico: "ico", icon: "ico",
};

/** "png", "JPG", "image/jpeg", "image/x-icon", ".webp" -> the format id, or null when unknown. */
export function parseFormat(input: unknown): OutFormat | null {
  if (typeof input !== "string") return null;
  let v = input.trim().toLowerCase().replace(/^\./, "");
  if (v.startsWith("image/")) v = v.slice(6);
  if (v === "x-icon" || v === "vnd.microsoft.icon") return "ico";
  if (v === "x-ms-bmp" || v === "x-bmp") return "bmp";
  if (v === "pjpeg") return "jpeg";
  return FORMAT_ALIASES[v] ?? null;
}

/** Output extension for a format id ("jpeg" -> "jpg"). */
export function extFor(id: OutFormat): string {
  return formatInfo(id).ext;
}

export const SUPPORTED_INPUTS_TEXT = "JPEG, PNG, WebP, GIF, AVIF, TIFF, SVG and BMP";
export const HEIC_UNSUPPORTED_MESSAGE =
  "HEIC/HEIF input is not supported by this service (the HEIC codec is patent-encumbered and is not included). Convert the photo to JPEG on the device first, or use the free in-browser Convert Image tool at freedailypro.com, which opens HEIC photos.";

// ---------------------------------------------------------------- colours

export interface RGBA { r: number; g: number; b: number; /** 0 (see-through) to 1 (solid) */ a: number }

const NAMED = "aliceblue:f0f8ff,antiquewhite:faebd7,aqua:00ffff,aquamarine:7fffd4,azure:f0ffff,beige:f5f5dc,bisque:ffe4c4,black:000000,blanchedalmond:ffebcd,blue:0000ff,blueviolet:8a2be2,brown:a52a2a,burlywood:deb887,cadetblue:5f9ea0,chartreuse:7fff00,chocolate:d2691e,coral:ff7f50,cornflowerblue:6495ed,cornsilk:fff8dc,crimson:dc143c,cyan:00ffff,darkblue:00008b,darkcyan:008b8b,darkgoldenrod:b8860b,darkgray:a9a9a9,darkgreen:006400,darkgrey:a9a9a9,darkkhaki:bdb76b,darkmagenta:8b008b,darkolivegreen:556b2f,darkorange:ff8c00,darkorchid:9932cc,darkred:8b0000,darksalmon:e9967a,darkseagreen:8fbc8f,darkslateblue:483d8b,darkslategray:2f4f4f,darkslategrey:2f4f4f,darkturquoise:00ced1,darkviolet:9400d3,deeppink:ff1493,deepskyblue:00bfff,dimgray:696969,dimgrey:696969,dodgerblue:1e90ff,firebrick:b22222,floralwhite:fffaf0,forestgreen:228b22,fuchsia:ff00ff,gainsboro:dcdcdc,ghostwhite:f8f8ff,gold:ffd700,goldenrod:daa520,gray:808080,green:008000,greenyellow:adff2f,grey:808080,honeydew:f0fff0,hotpink:ff69b4,indianred:cd5c5c,indigo:4b0082,ivory:fffff0,khaki:f0e68c,lavender:e6e6fa,lavenderblush:fff0f5,lawngreen:7cfc00,lemonchiffon:fffacd,lightblue:add8e6,lightcoral:f08080,lightcyan:e0ffff,lightgoldenrodyellow:fafad2,lightgray:d3d3d3,lightgreen:90ee90,lightgrey:d3d3d3,lightpink:ffb6c1,lightsalmon:ffa07a,lightseagreen:20b2aa,lightskyblue:87cefa,lightslategray:778899,lightslategrey:778899,lightsteelblue:b0c4de,lightyellow:ffffe0,lime:00ff00,limegreen:32cd32,linen:faf0e6,magenta:ff00ff,maroon:800000,mediumaquamarine:66cdaa,mediumblue:0000cd,mediumorchid:ba55d3,mediumpurple:9370db,mediumseagreen:3cb371,mediumslateblue:7b68ee,mediumspringgreen:00fa9a,mediumturquoise:48d1cc,mediumvioletred:c71585,midnightblue:191970,mintcream:f5fffa,mistyrose:ffe4e1,moccasin:ffe4b5,navajowhite:ffdead,navy:000080,oldlace:fdf5e6,olive:808000,olivedrab:6b8e23,orange:ffa500,orangered:ff4500,orchid:da70d6,palegoldenrod:eee8aa,palegreen:98fb98,paleturquoise:afeeee,palevioletred:db7093,papayawhip:ffefd5,peachpuff:ffdab9,peru:cd853f,pink:ffc0cb,plum:dda0dd,powderblue:b0e0e6,purple:800080,rebeccapurple:663399,red:ff0000,rosybrown:bc8f8f,royalblue:4169e1,saddlebrown:8b4513,salmon:fa8072,sandybrown:f4a460,seagreen:2e8b57,seashell:fff5ee,sienna:a0522d,silver:c0c0c0,skyblue:87ceeb,slateblue:6a5acd,slategray:708090,slategrey:708090,snow:fffafa,springgreen:00ff7f,steelblue:4682b4,tan:d2b48c,teal:008080,thistle:d8bfd8,tomato:ff6347,turquoise:40e0d0,violet:ee82ee,wheat:f5deb3,white:ffffff,whitesmoke:f5f5f5,yellow:ffff00,yellowgreen:9acd32";
export const NAMED_COLORS: Record<string, string> = Object.fromEntries(NAMED.split(",").map((p) => p.split(":") as [string, string]));

export const COLOR_HELP = "Use a hex value such as #ffffff, ffffff or #fff (8 digits add transparency), or a CSS colour name such as white.";

/** Parses "#fff", "ffffff", "%23ff0000", "#ff000080", "white", "transparent". Returns null when it is not a colour. */
export function parseColor(input: unknown): RGBA | null {
  if (typeof input !== "string") return null;
  const v = input.trim().replace(/^%23/i, "#");
  if (!v) return null;
  if (/^#?([0-9a-f]{3,4}|[0-9a-f]{6}|[0-9a-f]{8})$/i.test(v)) {
    let h = v.replace(/^#/, "");
    if (h.length <= 4) h = h.split("").map((c) => c + c).join("");
    const n = (i: number) => parseInt(h.slice(i, i + 2), 16);
    return { r: n(0), g: n(2), b: n(4), a: h.length === 8 ? n(6) / 255 : 1 };
  }
  const name = v.toLowerCase();
  if (name === "transparent") return { r: 0, g: 0, b: 0, a: 0 };
  const hex = NAMED_COLORS[name];
  if (hex) return { r: parseInt(hex.slice(0, 2), 16), g: parseInt(hex.slice(2, 4), 16), b: parseInt(hex.slice(4, 6), 16), a: 1 };
  return null;
}

export function rgbaToHex(c: RGBA): string {
  const h = (n: number) => Math.round(Math.min(255, Math.max(0, n))).toString(16).padStart(2, "0");
  return `#${h(c.r)}${h(c.g)}${h(c.b)}`;
}

// ---------------------------------------------------------------- option validation

export type FitMode = "cover" | "contain" | "fill" | "inside" | "outside";
export const FIT_MODES: readonly FitMode[] = ["cover", "contain", "fill", "inside", "outside"];
export const ICO_SIZES = [16, 32, 48, 64, 128, 256] as const;
export const MAX_DIMENSION = 16384;
export type TiffCompression = "none" | "lzw" | "deflate" | "jpeg";

export interface ConvertOptions {
  format: OutFormat;
  /** 1-100 */
  quality?: number;
  /** Fill for see-through pixels; undefined when the caller did not choose one. */
  backgroundColor?: RGBA;
  lossless?: boolean;
  progressive?: boolean;
  chromaSubsampling?: "4:4:4" | "4:2:0";
  pngCompressionLevel?: number;
  pngPalette?: boolean;
  /** CPU effort, 0 (fast) to 10 (smallest); clamped to what the format allows. */
  effort?: number;
  width?: number;
  height?: number;
  fit: FitMode;
  withoutEnlargement: boolean;
  autoOrient: boolean;
  stripMetadata: boolean;
  grayscale: boolean;
  dpi?: number;
  rotate: 0 | 90 | 180 | 270;
  /** Mirror top to bottom. */
  flip: boolean;
  /** Mirror left to right. */
  flop: boolean;
  sharpen?: number;
  blur?: number;
  /** ICO: pixel sizes packed into the file. */
  sizes: number[];
  /** BMP: 24 (no transparency) or 32 (keeps transparency). */
  bitDepth: 24 | 32;
  tiffCompression: TiffCompression;
  /** Keep every frame of an animated GIF/WebP when the output is GIF or WebP. */
  animated: boolean;
}

export type ParsedOptions = { ok: true; options: ConvertOptions } | { ok: false; error: string };

const isBlank = (v: unknown) => v === undefined || v === null || (typeof v === "string" && v.trim() === "");

/** 0-1 or 1-100 -> integer 1-100; null when invalid. 1 means 100% (use 2 or more for "quality 2"). */
export function normalizeQuality(v: unknown): number | null {
  const n = typeof v === "number" ? v : typeof v === "string" && v.trim() !== "" ? Number(v) : NaN;
  if (!Number.isFinite(n) || n < 0 || n > 100) return null;
  return n <= 1 ? Math.max(1, Math.round(n * 100)) : Math.round(n);
}

function parseBool(v: unknown): boolean | null {
  if (typeof v === "boolean") return v;
  if (typeof v === "number") return v === 1 ? true : v === 0 ? false : null;
  if (typeof v === "string") {
    const s = v.trim().toLowerCase();
    if (["true", "1", "yes", "on"].includes(s)) return true;
    if (["false", "0", "no", "off"].includes(s)) return false;
  }
  return null;
}

function parseNum(v: unknown): number | null {
  const n = typeof v === "number" ? v : typeof v === "string" && v.trim() !== "" ? Number(v) : NaN;
  return Number.isFinite(n) ? n : null;
}

export function parseIcoSizes(v: unknown): number[] | null {
  const parts: unknown[] = Array.isArray(v) ? v : typeof v === "string" ? v.split(/[\s,;x]+/).filter(Boolean) : typeof v === "number" ? [v] : [];
  if (!parts.length) return null;
  const out = new Set<number>();
  for (const p of parts) {
    const n = parseNum(p);
    if (n === null || !(ICO_SIZES as readonly number[]).includes(n)) return null;
    out.add(n);
  }
  return Array.from(out).sort((a, b) => a - b);
}

/**
 * Validates the raw option values (from multipart form fields, a JSON body or an MCP call; strings are fine) and
 * returns clean options, or one plain-language error naming the field. Options that do not apply to the chosen
 * format are accepted and ignored.
 */
export function parseConvertOptions(raw: Record<string, unknown>): ParsedOptions {
  const err = (error: string): ParsedOptions => ({ ok: false, error });
  const format = parseFormat(raw.format);
  if (!format) return err(`'format' must be one of ${FORMAT_IDS.join(", ")} (an image/... type such as image/webp also works).`);

  const o: ConvertOptions = {
    format, fit: "inside", withoutEnlargement: false, autoOrient: true, stripMetadata: true, grayscale: false,
    rotate: 0, flip: false, flop: false, sizes: [16, 32, 48], bitDepth: 24, tiffCompression: "lzw", animated: true,
  };

  const bool = (key: string, set: (b: boolean) => void): string | null => {
    const v = raw[key];
    if (isBlank(v)) return null;
    const b = parseBool(v);
    if (b === null) return `'${key}' must be true or false.`;
    set(b);
    return null;
  };
  const int = (key: string, min: number, max: number, set: (n: number) => void): string | null => {
    const v = raw[key];
    if (isBlank(v)) return null;
    const n = parseNum(v);
    if (n === null || !Number.isInteger(n) || n < min || n > max) return `'${key}' must be a whole number from ${min} to ${max}.`;
    set(n);
    return null;
  };

  if (!isBlank(raw.quality)) {
    const q = normalizeQuality(raw.quality);
    if (q === null) return err("'quality' must be between 0 and 1 (for example 0.8) or between 1 and 100 (for example 80).");
    o.quality = q;
  }
  const bg = raw.backgroundColor ?? raw.background;
  if (!isBlank(bg)) {
    const c = parseColor(bg);
    if (!c) return err(`'backgroundColor' "${String(bg)}" is not a colour. ${COLOR_HELP}`);
    o.backgroundColor = c;
  }
  const checks = [
    bool("lossless", (b) => (o.lossless = b)),
    bool("progressive", (b) => (o.progressive = b)),
    bool("pngPalette", (b) => (o.pngPalette = b)),
    bool("withoutEnlargement", (b) => (o.withoutEnlargement = b)),
    bool("autoOrient", (b) => (o.autoOrient = b)),
    bool("stripMetadata", (b) => (o.stripMetadata = b)),
    bool("grayscale", (b) => (o.grayscale = b)),
    bool("flip", (b) => (o.flip = b)),
    bool("flop", (b) => (o.flop = b)),
    bool("animated", (b) => (o.animated = b)),
    int("pngCompressionLevel", 0, 9, (n) => (o.pngCompressionLevel = n)),
    int("width", 1, MAX_DIMENSION, (n) => (o.width = n)),
    int("height", 1, MAX_DIMENSION, (n) => (o.height = n)),
    int("dpi", 1, 2400, (n) => (o.dpi = n)),
  ];
  const effortRaw = raw.effort ?? raw.speed;
  if (!isBlank(effortRaw)) {
    const n = parseNum(effortRaw);
    if (n === null || !Number.isInteger(n) || n < 0 || n > 10) checks.push("'effort' must be a whole number from 0 (fastest) to 10 (smallest file). 'speed' is accepted as the same setting.");
    else o.effort = n;
  }
  const firstErr = checks.find((c) => c);
  if (firstErr) return err(firstErr);

  if (!isBlank(raw.chromaSubsampling)) {
    const s = String(raw.chromaSubsampling).trim();
    if (s !== "4:4:4" && s !== "4:2:0") return err("'chromaSubsampling' must be 4:4:4 (sharpest colour edges) or 4:2:0 (smaller file).");
    o.chromaSubsampling = s;
  }
  if (!isBlank(raw.fit)) {
    const f = String(raw.fit).trim().toLowerCase();
    if (!(FIT_MODES as readonly string[]).includes(f)) return err(`'fit' must be one of ${FIT_MODES.join(", ")}.`);
    o.fit = f as FitMode;
  }
  if (!isBlank(raw.rotate)) {
    const n = parseNum(raw.rotate);
    if (n === null || !Number.isInteger(n) || n % 90 !== 0) return err("'rotate' must be a multiple of 90 degrees (90, 180, 270, or -90).");
    o.rotate = ((((n % 360) + 360) % 360) as 0 | 90 | 180 | 270);
  }
  for (const key of ["sharpen", "blur"] as const) {
    const v = raw[key];
    if (isBlank(v)) continue;
    const b = parseBool(v);
    if (b === false) continue;
    const n = b === true ? (key === "blur" ? 2 : 1) : parseNum(v);
    if (n === null || n < 0.3 || n > 100) return err(`'${key}' must be true, or an amount (sigma) between 0.3 and 100.`);
    o[key] = n;
  }
  if (!isBlank(raw.sizes)) {
    const s = parseIcoSizes(raw.sizes);
    if (!s) return err(`'sizes' must list ICO sizes from ${ICO_SIZES.join(", ")}, for example "16,32,48".`);
    o.sizes = s;
  }
  if (!isBlank(raw.bitDepth)) {
    const n = parseNum(raw.bitDepth);
    if (n !== 24 && n !== 32) return err("'bitDepth' must be 24 (BMP without transparency) or 32 (BMP with transparency).");
    o.bitDepth = n;
  }
  if (!isBlank(raw.tiffCompression ?? raw.compression)) {
    const s = String(raw.tiffCompression ?? raw.compression).trim().toLowerCase();
    if (!["none", "lzw", "deflate", "jpeg"].includes(s)) return err("'tiffCompression' must be none, lzw, deflate or jpeg.");
    o.tiffCompression = s as TiffCompression;
  }
  return { ok: true, options: o };
}

// ---------------------------------------------------------------- resize geometry (canvas)

export interface FitPlan {
  /** Size of the finished picture. */
  outW: number;
  outH: number;
  /** Source rectangle copied ... */
  sx: number; sy: number; sw: number; sh: number;
  /** ... into this rectangle of the output (the rest is padding). */
  dx: number; dy: number; dw: number; dh: number;
}

/**
 * Where the source goes in the output for each fit mode (same meanings as the API):
 * cover = fill the box, crop the overflow; contain = fit inside the box, pad the rest; fill = stretch to the box;
 * inside = fit within the box keeping the ratio (no padding); outside = at least as large as the box, keeping the ratio.
 * With one side missing the other decides and the ratio is kept. withoutEnlargement never makes the picture bigger.
 */
export function computeFit(srcW: number, srcH: number, w: number | undefined, h: number | undefined, fit: FitMode, withoutEnlargement = false): FitPlan {
  const whole = (outW: number, outH: number): FitPlan => ({ outW, outH, sx: 0, sy: 0, sw: srcW, sh: srcH, dx: 0, dy: 0, dw: outW, dh: outH });
  if (!w && !h) return whole(srcW, srcH);
  const R = (n: number) => Math.max(1, Math.round(n));
  if (!w || !h) {
    let scale = w ? w / srcW : (h as number) / srcH;
    if (withoutEnlargement) scale = Math.min(1, scale);
    return whole(R(srcW * scale), R(srcH * scale));
  }
  let bw = w, bh = h;
  if (fit === "fill") {
    if (withoutEnlargement) { bw = Math.min(bw, srcW); bh = Math.min(bh, srcH); }
    return whole(bw, bh);
  }
  const sMin = Math.min(bw / srcW, bh / srcH);
  const sMax = Math.max(bw / srcW, bh / srcH);
  if (fit === "inside" || fit === "outside") {
    let s = fit === "inside" ? sMin : sMax;
    if (withoutEnlargement) s = Math.min(1, s);
    return whole(R(srcW * s), R(srcH * s));
  }
  if (fit === "contain") {
    let s = sMin;
    if (withoutEnlargement && s > 1) { s = 1; bw = srcW; bh = srcH; }
    const dw = R(srcW * s), dh = R(srcH * s);
    return { outW: bw, outH: bh, sx: 0, sy: 0, sw: srcW, sh: srcH, dx: Math.round((bw - dw) / 2), dy: Math.round((bh - dh) / 2), dw, dh };
  }
  // cover
  let s = sMax;
  if (withoutEnlargement && s > 1) { s = 1; bw = Math.min(bw, srcW); bh = Math.min(bh, srcH); }
  const cw = Math.min(srcW, bw / s), ch = Math.min(srcH, bh / s);
  return { outW: bw, outH: bh, sx: (srcW - cw) / 2, sy: (srcH - ch) / 2, sw: cw, sh: ch, dx: 0, dy: 0, dw: bw, dh: bh };
}

// ---------------------------------------------------------------- small byte helpers

type Pixels = Uint8Array | Uint8ClampedArray;

function checkPixels(rgba: Pixels, w: number, h: number) {
  if (!Number.isInteger(w) || !Number.isInteger(h) || w < 1 || h < 1) throw new Error("The image has no size.");
  if (rgba.length < w * h * 4) throw new Error("Pixel data is shorter than the image size.");
}

/** Composite one pixel over a solid colour; returns [r,g,b]. */
function over(r: number, g: number, b: number, a: number, bg: RGBA): [number, number, number] {
  if (a >= 255) return [r, g, b];
  const t = a / 255;
  return [Math.round(r * t + bg.r * (1 - t)), Math.round(g * t + bg.g * (1 - t)), Math.round(b * t + bg.b * (1 - t))];
}
const WHITE: RGBA = { r: 255, g: 255, b: 255, a: 1 };

// ---------------------------------------------------------------- BMP

/** Writes an uncompressed BMP. 24-bit flattens transparency onto `background` (white by default); 32-bit keeps alpha. */
export function encodeBmp(rgba: Pixels, w: number, h: number, opts: { bits?: 24 | 32; background?: RGBA; dpi?: number } = {}): Uint8Array {
  checkPixels(rgba, w, h);
  const bits = opts.bits ?? 24;
  const bg = opts.background ?? WHITE;
  const ppm = Math.round(((opts.dpi ?? 72) * 1000) / 25.4);
  const headerSize = bits === 32 ? 108 : 40;
  const rowBytes = bits === 32 ? w * 4 : Math.ceil((w * 3) / 4) * 4;
  const dataSize = rowBytes * h;
  const offset = 14 + headerSize;
  const out = new Uint8Array(offset + dataSize);
  const dv = new DataView(out.buffer);
  out[0] = 0x42; out[1] = 0x4d;
  dv.setUint32(2, out.length, true);
  dv.setUint32(10, offset, true);
  dv.setUint32(14, headerSize, true);
  dv.setInt32(18, w, true);
  dv.setInt32(22, h, true);
  dv.setUint16(26, 1, true);
  dv.setUint16(28, bits, true);
  dv.setUint32(30, bits === 32 ? 3 : 0, true); // BI_BITFIELDS for 32-bit so alpha is honoured
  dv.setUint32(34, dataSize, true);
  dv.setInt32(38, ppm, true);
  dv.setInt32(42, ppm, true);
  if (bits === 32) {
    dv.setUint32(54, 0x00ff0000, true); dv.setUint32(58, 0x0000ff00, true); dv.setUint32(62, 0x000000ff, true); dv.setUint32(66, 0xff000000, true);
    dv.setUint32(70, 0x73524742, true); // "sRGB"
  }
  for (let y = 0; y < h; y++) {
    let p = offset + (h - 1 - y) * rowBytes;
    for (let x = 0; x < w; x++) {
      const i = (y * w + x) * 4;
      if (bits === 32) {
        out[p++] = rgba[i + 2]; out[p++] = rgba[i + 1]; out[p++] = rgba[i]; out[p++] = rgba[i + 3];
      } else {
        const [r, g, b] = over(rgba[i], rgba[i + 1], rgba[i + 2], rgba[i + 3], bg);
        out[p++] = b; out[p++] = g; out[p++] = r;
      }
    }
  }
  return out;
}

export interface DecodedPixels { width: number; height: number; rgba: Uint8Array }

export function isBmp(b: Uint8Array): boolean {
  return b.length > 30 && b[0] === 0x42 && b[1] === 0x4d;
}

/** Reads an uncompressed BMP (1, 4, 8, 16, 24 or 32 bits, with or without bit masks). RLE-compressed files are refused. */
export function decodeBmp(b: Uint8Array): DecodedPixels {
  if (!isBmp(b)) throw new Error("This is not a BMP file.");
  const dv = new DataView(b.buffer, b.byteOffset, b.byteLength);
  const dataOffset = dv.getUint32(10, true);
  const hs = dv.getUint32(14, true);
  let w: number, h: number, bits: number, comp = 0, colorsUsed = 0, palEntry = 4;
  if (hs === 12) {
    w = dv.getUint16(18, true); h = dv.getUint16(20, true); bits = dv.getUint16(24, true); palEntry = 3;
  } else if (hs >= 40) {
    w = dv.getInt32(18, true); h = dv.getInt32(22, true); bits = dv.getUint16(28, true); comp = dv.getUint32(30, true); colorsUsed = dv.getUint32(46, true);
  } else throw new Error("This BMP file has an unknown header.");
  const topDown = h < 0;
  h = Math.abs(h);
  if (w < 1 || h < 1 || w > 30000 || h > 30000) throw new Error("This BMP file has an invalid size.");
  if (comp === 1 || comp === 2) throw new Error("This BMP uses RLE compression, which is not supported. Re-save it as an uncompressed BMP or PNG first.");
  if (comp !== 0 && comp !== 3 && comp !== 6) throw new Error("This BMP uses a compression type that is not supported.");
  if (![1, 4, 8, 16, 24, 32].includes(bits)) throw new Error("This BMP has an unsupported colour depth.");

  const palette: number[][] = [];
  if (bits <= 8) {
    const n = colorsUsed || 1 << bits;
    const palStart = 14 + hs;
    for (let i = 0; i < n; i++) {
      const p = palStart + i * palEntry;
      if (p + 2 >= b.length) break;
      palette.push([b[p + 2], b[p + 1], b[p]]);
    }
  }
  let masks = [0x00ff0000, 0x0000ff00, 0x000000ff, 0];
  if (bits === 16) masks = [0x7c00, 0x03e0, 0x001f, 0];
  if (comp === 3 || comp === 6) {
    const mp = hs >= 52 ? 54 : 54; // masks follow the 40-byte header (or live inside larger headers at 54)
    masks = [dv.getUint32(mp, true), dv.getUint32(mp + 4, true), dv.getUint32(mp + 8, true), hs >= 56 || comp === 6 ? dv.getUint32(mp + 12, true) : 0];
  }
  const rowBytes = Math.ceil((w * bits) / 32) * 4;
  if (dataOffset + rowBytes * h > b.length + 3) throw new Error("This BMP file is cut short.");
  const out = new Uint8Array(w * h * 4);
  const ch = (v: number, mask: number) => {
    if (!mask) return 255;
    let shift = 0; while (!((mask >>> shift) & 1)) shift++;
    const max = mask >>> shift;
    return Math.round((((v & mask) >>> shift) * 255) / max);
  };
  let anyAlpha = false;
  for (let y = 0; y < h; y++) {
    const row = dataOffset + (topDown ? y : h - 1 - y) * rowBytes;
    for (let x = 0; x < w; x++) {
      const o = (y * w + x) * 4;
      let r = 0, g = 0, bl = 0, a = 255;
      if (bits <= 8) {
        const bitPos = x * bits;
        const byte = b[row + (bitPos >> 3)];
        const idx = (byte >> (8 - bits - (bitPos & 7))) & ((1 << bits) - 1);
        const c = palette[idx] ?? [0, 0, 0];
        [r, g, bl] = c;
      } else if (bits === 24) {
        bl = b[row + x * 3]; g = b[row + x * 3 + 1]; r = b[row + x * 3 + 2];
      } else {
        const v = bits === 16 ? dv.getUint16(row + x * 2, true) : dv.getUint32(row + x * 4, true);
        r = ch(v, masks[0]); g = ch(v, masks[1]); bl = ch(v, masks[2]);
        if (masks[3]) { a = ch(v, masks[3]); if (a) anyAlpha = true; }
      }
      out[o] = r; out[o + 1] = g; out[o + 2] = bl; out[o + 3] = a;
    }
  }
  // 32-bit files without a real alpha channel often store 0 in the spare byte: treat as opaque.
  if (bits === 32 && masks[3] && !anyAlpha) for (let i = 3; i < out.length; i += 4) out[i] = 255;
  return { width: w, height: h, rgba: out };
}

// ---------------------------------------------------------------- ICO

/** Packs PNG images (one per size) into an ICO container. Sizes 256 and above are stored as 0 per the format. */
export function encodeIco(entries: { size: number; png: Uint8Array }[]): Uint8Array {
  if (!entries.length) throw new Error("An icon needs at least one size.");
  const head = 6 + entries.length * 16;
  const total = head + entries.reduce((n, e) => n + e.png.length, 0);
  const out = new Uint8Array(total);
  const dv = new DataView(out.buffer);
  dv.setUint16(2, 1, true);
  dv.setUint16(4, entries.length, true);
  let offset = head;
  entries.forEach((e, i) => {
    const p = 6 + i * 16;
    out[p] = e.size >= 256 ? 0 : e.size;
    out[p + 1] = e.size >= 256 ? 0 : e.size;
    dv.setUint16(p + 4, 1, true);
    dv.setUint16(p + 6, 32, true);
    dv.setUint32(p + 8, e.png.length, true);
    dv.setUint32(p + 12, offset, true);
    out.set(e.png, offset);
    offset += e.png.length;
  });
  return out;
}

/** Sizes stored in an ICO file (for checks and tests). */
export function readIcoSizes(b: Uint8Array): number[] {
  if (b.length < 6 || b[2] !== 1 || b[3] !== 0) return [];
  const n = b[4] | (b[5] << 8);
  const sizes: number[] = [];
  for (let i = 0; i < n; i++) sizes.push(b[6 + i * 16] || 256);
  return sizes;
}

// ---------------------------------------------------------------- TIFF

/** Writes a plain uncompressed TIFF (8 bits per channel). With `alpha` the transparency is kept; otherwise it is flattened on `background`. */
export function encodeTiff(rgba: Pixels, w: number, h: number, opts: { alpha?: boolean; background?: RGBA; dpi?: number } = {}): Uint8Array {
  checkPixels(rgba, w, h);
  const spp = opts.alpha ? 4 : 3;
  const bg = opts.background ?? WHITE;
  const dpi = opts.dpi ?? 72;
  const entries = opts.alpha ? 13 : 12;
  const ifdSize = 2 + entries * 12 + 4;
  const bpsOff = 8 + ifdSize;
  const resOff = bpsOff + spp * 2;
  const dataOff = resOff + 16;
  const dataSize = w * h * spp;
  const out = new Uint8Array(dataOff + dataSize);
  const dv = new DataView(out.buffer);
  out[0] = 0x49; out[1] = 0x49; dv.setUint16(2, 42, true); dv.setUint32(4, 8, true);
  let p = 8;
  dv.setUint16(p, entries, true); p += 2;
  const entry = (tag: number, type: number, count: number, value: number) => {
    dv.setUint16(p, tag, true); dv.setUint16(p + 2, type, true); dv.setUint32(p + 4, count, true);
    if (type === 3 && count === 1) dv.setUint16(p + 8, value, true); else dv.setUint32(p + 8, value, true);
    p += 12;
  };
  entry(256, 4, 1, w);
  entry(257, 4, 1, h);
  entry(258, 3, spp, bpsOff);
  entry(259, 3, 1, 1);
  entry(262, 3, 1, 2);
  entry(273, 4, 1, dataOff);
  entry(277, 3, 1, spp);
  entry(278, 4, 1, h);
  entry(279, 4, 1, dataSize);
  entry(282, 5, 1, resOff);
  entry(283, 5, 1, resOff + 8);
  entry(296, 3, 1, 2);
  if (opts.alpha) entry(338, 3, 1, 2); // ExtraSamples: unassociated alpha
  dv.setUint32(p, 0, true);
  for (let i = 0; i < spp; i++) dv.setUint16(bpsOff + i * 2, 8, true);
  dv.setUint32(resOff, dpi, true); dv.setUint32(resOff + 4, 1, true);
  dv.setUint32(resOff + 8, dpi, true); dv.setUint32(resOff + 12, 1, true);
  let q = dataOff;
  for (let i = 0; i < w * h; i++) {
    const s = i * 4;
    if (opts.alpha) { out[q++] = rgba[s]; out[q++] = rgba[s + 1]; out[q++] = rgba[s + 2]; out[q++] = rgba[s + 3]; }
    else { const [r, g, b] = over(rgba[s], rgba[s + 1], rgba[s + 2], rgba[s + 3], bg); out[q++] = r; out[q++] = g; out[q++] = b; }
  }
  return out;
}

// ---------------------------------------------------------------- GIF (single frame)

interface Box { keys: number[]; }

/** Reduces an image to at most `maxColors` colours (median cut over 5-bit colour bins). */
export function quantize(rgba: Pixels, count: number, maxColors: number, isOpaque: (i: number) => boolean): { palette: number[][]; indices: Uint8Array; transparentIndex: number } {
  const bins = new Map<number, { n: number; r: number; g: number; b: number }>();
  const keyAt = new Int32Array(count);
  for (let i = 0; i < count; i++) {
    if (!isOpaque(i)) { keyAt[i] = -1; continue; }
    const r = rgba[i * 4], g = rgba[i * 4 + 1], b = rgba[i * 4 + 2];
    const key = ((r >> 3) << 10) | ((g >> 3) << 5) | (b >> 3);
    keyAt[i] = key;
    const e = bins.get(key);
    if (e) { e.n++; e.r += r; e.g += g; e.b += b; } else bins.set(key, { n: 1, r, g, b });
  }
  const avg = (key: number) => { const e = bins.get(key)!; return [e.r / e.n, e.g / e.n, e.b / e.n]; };
  const boxes: Box[] = [{ keys: Array.from(bins.keys()) }];
  const range = (bx: Box) => {
    let min = [255, 255, 255], max = [0, 0, 0];
    for (const k of bx.keys) { const c = avg(k); for (let a = 0; a < 3; a++) { if (c[a] < min[a]) min[a] = c[a]; if (c[a] > max[a]) max[a] = c[a]; } }
    const spans = [max[0] - min[0], max[1] - min[1], max[2] - min[2]];
    const axis = spans[0] >= spans[1] && spans[0] >= spans[2] ? 0 : spans[1] >= spans[2] ? 1 : 2;
    return { axis, span: spans[axis] };
  };
  const pop = (bx: Box) => bx.keys.reduce((n, k) => n + bins.get(k)!.n, 0);
  while (boxes.length < maxColors) {
    let best = -1, bestScore = 0, bestAxis = 0;
    boxes.forEach((bx, i) => {
      if (bx.keys.length < 2) return;
      const { axis, span } = range(bx);
      const score = span * Math.sqrt(pop(bx));
      if (score > bestScore) { best = i; bestScore = score; bestAxis = axis; }
    });
    if (best < 0) break;
    const bx = boxes[best];
    bx.keys.sort((a, b) => avg(a)[bestAxis] - avg(b)[bestAxis]);
    const half = pop(bx) / 2;
    let acc = 0, cut = 1;
    for (let i = 0; i < bx.keys.length - 1; i++) { acc += bins.get(bx.keys[i])!.n; cut = i + 1; if (acc >= half) break; }
    boxes.splice(best, 1, { keys: bx.keys.slice(0, cut) }, { keys: bx.keys.slice(cut) });
  }
  const palette = boxes.map((bx) => {
    let n = 0, r = 0, g = 0, b = 0;
    for (const k of bx.keys) { const e = bins.get(k)!; n += e.n; r += e.r; g += e.g; b += e.b; }
    return n ? [Math.round(r / n), Math.round(g / n), Math.round(b / n)] : [0, 0, 0];
  });
  const nearest = new Map<number, number>();
  const lookup = (key: number) => {
    let hit = nearest.get(key);
    if (hit !== undefined) return hit;
    const c = avg(key);
    let bestD = Infinity; hit = 0;
    for (let i = 0; i < palette.length; i++) {
      const d = (palette[i][0] - c[0]) ** 2 + (palette[i][1] - c[1]) ** 2 + (palette[i][2] - c[2]) ** 2;
      if (d < bestD) { bestD = d; hit = i; }
    }
    nearest.set(key, hit);
    return hit;
  };
  const transparentIndex = -1;
  const indices = new Uint8Array(count);
  let hasTransparent = false;
  for (let i = 0; i < count; i++) {
    if (keyAt[i] < 0) { hasTransparent = true; indices[i] = 255; } else indices[i] = lookup(keyAt[i]);
  }
  if (hasTransparent) {
    const t = palette.length;
    palette.push([0, 0, 0]);
    for (let i = 0; i < count; i++) if (keyAt[i] < 0) indices[i] = t;
    return { palette, indices, transparentIndex: t };
  }
  return { palette, indices, transparentIndex };
}

function lzwEncode(indices: Uint8Array, minCode: number): number[] {
  const clear = 1 << minCode, eoi = clear + 1;
  const bytes: number[] = [];
  let cur = 0, shift = 0, size = minCode + 1, next = eoi + 1;
  let table = new Map<number, number>();
  const emit = (code: number) => {
    cur |= code << shift; shift += size;
    while (shift >= 8) { bytes.push(cur & 255); cur >>>= 8; shift -= 8; }
  };
  emit(clear);
  let code = indices[0];
  for (let i = 1; i < indices.length; i++) {
    const k = indices[i];
    const key = (code << 8) | k;
    const hit = table.get(key);
    if (hit === undefined) {
      emit(code);
      if (next === 4096) { emit(clear); table = new Map(); next = eoi + 1; size = minCode + 1; }
      else { if (next >= 1 << size) size++; table.set(key, next++); }
      code = k;
    } else code = hit;
  }
  emit(code);
  emit(eoi);
  if (shift > 0) bytes.push(cur & 255);
  return bytes;
}

/**
 * Writes a single-frame GIF (256 colours at most). Transparency: pixels under 50% opaque become see-through unless a
 * `background` is given, in which case everything is flattened onto that colour.
 */
export function encodeGif(rgba: Pixels, w: number, h: number, opts: { background?: RGBA } = {}): Uint8Array {
  checkPixels(rgba, w, h);
  const count = w * h;
  const flat = opts.background;
  const px = flat ? new Uint8Array(count * 4) : rgba;
  if (flat) for (let i = 0; i < count; i++) { const [r, g, b] = over(rgba[i * 4], rgba[i * 4 + 1], rgba[i * 4 + 2], rgba[i * 4 + 3], flat); px[i * 4] = r; px[i * 4 + 1] = g; px[i * 4 + 2] = b; px[i * 4 + 3] = 255; }
  const { palette, indices, transparentIndex } = quantize(px, count, 255, (i) => px[i * 4 + 3] >= 128);
  let bitsPer = 1; while ((1 << bitsPer) < palette.length) bitsPer++;
  const tableSize = 1 << bitsPer;
  const minCode = Math.max(2, bitsPer);
  const lzw = lzwEncode(indices, minCode);
  const out: number[] = [0x47, 0x49, 0x46, 0x38, 0x39, 0x61, w & 255, w >> 8, h & 255, h >> 8, 0x80 | 0x70 | (bitsPer - 1), 0, 0];
  for (let i = 0; i < tableSize; i++) out.push(...(palette[i] ?? [0, 0, 0]));
  if (transparentIndex >= 0) out.push(0x21, 0xf9, 4, 0x01, 0, 0, transparentIndex, 0);
  out.push(0x2c, 0, 0, 0, 0, w & 255, w >> 8, h & 255, h >> 8, 0, minCode);
  for (let i = 0; i < lzw.length; i += 255) { const chunk = lzw.slice(i, i + 255); out.push(chunk.length, ...chunk); }
  out.push(0, 0x3b);
  return Uint8Array.from(out);
}

// ---------------------------------------------------------------- PNG / JPEG metadata

let crcTable: Uint32Array | null = null;
export function crc32(bytes: Uint8Array): number {
  if (!crcTable) {
    crcTable = new Uint32Array(256);
    for (let n = 0; n < 256; n++) { let c = n; for (let k = 0; k < 8; k++) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1; crcTable[n] = c >>> 0; }
  }
  let c = 0xffffffff;
  for (let i = 0; i < bytes.length; i++) c = crcTable[(c ^ bytes[i]) & 255] ^ (c >>> 8);
  return (c ^ 0xffffffff) >>> 0;
}

/** Writes the resolution into a PNG (pHYs chunk), replacing any existing one. */
export function setPngDpi(bytes: Uint8Array, dpi: number): Uint8Array {
  const sig = [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a];
  if (bytes.length < 33 || sig.some((v, i) => bytes[i] !== v)) return bytes;
  const ppm = Math.round(dpi / 0.0254);
  const chunk = new Uint8Array(21);
  const dv = new DataView(chunk.buffer);
  dv.setUint32(0, 9); chunk.set([0x70, 0x48, 0x59, 0x73], 4); dv.setUint32(8, ppm); dv.setUint32(12, ppm); chunk[16] = 1;
  dv.setUint32(17, crc32(chunk.subarray(4, 17)));
  const parts: Uint8Array[] = [bytes.subarray(0, 8)];
  let p = 8, inserted = false;
  while (p + 8 <= bytes.length) {
    const len = new DataView(bytes.buffer, bytes.byteOffset + p, 4).getUint32(0);
    const type = String.fromCharCode(bytes[p + 4], bytes[p + 5], bytes[p + 6], bytes[p + 7]);
    const end = p + 12 + len;
    if (type !== "pHYs") parts.push(bytes.subarray(p, end));
    if (type === "IHDR" && !inserted) { parts.push(chunk); inserted = true; }
    p = end;
  }
  const out = new Uint8Array(parts.reduce((n, a) => n + a.length, 0));
  let o = 0; for (const a of parts) { out.set(a, o); o += a.length; }
  return out;
}

/** The Exif APP1 segment of a JPEG (marker, length and payload), or null. */
export function extractJpegExif(b: Uint8Array): Uint8Array | null {
  if (b.length < 4 || b[0] !== 0xff || b[1] !== 0xd8) return null;
  let i = 2;
  while (i + 4 < b.length && b[i] === 0xff) {
    const m = b[i + 1];
    if (m === 0xda || m === 0xd9) break;
    const len = (b[i + 2] << 8) | b[i + 3];
    if (m === 0xe1 && b[i + 4] === 0x45 && b[i + 5] === 0x78 && b[i + 6] === 0x69 && b[i + 7] === 0x66) return b.slice(i, i + 2 + len);
    i += 2 + len;
  }
  return null;
}

/** Sets the Exif orientation tag to 1 (upright) inside an APP1 segment, in a copy. Used after the pixels were turned. */
export function resetExifOrientation(seg: Uint8Array): Uint8Array {
  const out = new Uint8Array(seg);
  const t = 10; // marker(2) + length(2) + "Exif\0\0"(6)
  if (out.length < t + 8) return out;
  const le = out[t] === 0x49;
  const dv = new DataView(out.buffer);
  const u16 = (o: number) => dv.getUint16(o, le);
  const ifd = t + dv.getUint32(t + 4, le);
  if (ifd + 2 > out.length) return out;
  const n = u16(ifd);
  for (let k = 0; k < n; k++) {
    const e = ifd + 2 + k * 12;
    if (e + 12 > out.length) break;
    if (u16(e) === 0x0112) { dv.setUint16(e + 8, 1, le); break; }
  }
  return out;
}

/** Inserts an Exif APP1 segment right after SOI (and after a JFIF header if there is one). */
export function insertJpegExif(jpeg: Uint8Array, seg: Uint8Array): Uint8Array {
  if (jpeg.length < 4 || jpeg[0] !== 0xff || jpeg[1] !== 0xd8) return jpeg;
  let at = 2;
  if (jpeg[2] === 0xff && jpeg[3] === 0xe0) at = 4 + ((jpeg[4] << 8) | jpeg[5]);
  const out = new Uint8Array(jpeg.length + seg.length);
  out.set(jpeg.subarray(0, at), 0);
  out.set(seg, at);
  out.set(jpeg.subarray(at), at + seg.length);
  return out;
}

/** Bytes of a PNG/JPEG/GIF/etc. start with the right signature for the format? (tests and sanity checks) */
export function matchesFormat(b: Uint8Array, id: OutFormat): boolean {
  const s = (i: number, str: string) => str.split("").every((c, k) => b[i + k] === c.charCodeAt(0));
  switch (id) {
    case "png": return b[0] === 0x89 && s(1, "PNG");
    case "jpeg": return b[0] === 0xff && b[1] === 0xd8 && b[2] === 0xff;
    case "gif": return s(0, "GIF8");
    case "webp": return s(0, "RIFF") && s(8, "WEBP");
    case "avif": return s(4, "ftyp") && (s(8, "avif") || s(8, "avis"));
    case "bmp": return b[0] === 0x42 && b[1] === 0x4d;
    case "ico": return b[0] === 0 && b[1] === 0 && b[2] === 1 && b[3] === 0;
    case "tiff": return (b[0] === 0x49 && b[1] === 0x49 && b[2] === 42 && b[3] === 0) || (b[0] === 0x4d && b[1] === 0x4d && b[2] === 0 && b[3] === 42);
  }
}
